import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, type FC } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Menu } from '@eqi-investimentos/icons/web';
import { Box, Flex, useColorMode } from '@eqi-investimentos/react';
import { Divider } from '@chakra-ui/react';
import { balanceFactory } from '@/modules/Gamification/interface/Gamification';
import { PATHS } from '@/config/paths';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { MY_PROFILE_DRAWER_ID } from '@/modules/Customer/ui/ProfileDrawer/utils';
import { useGetGamificationBalance } from '@/modules/Gamification/http/client/useGetGamificationBalance';
import { useAuth } from '@/contexts/AuthContext';
import { Icon } from '@/components/atoms/Icon';
import { IconButton } from '@/components/atoms/IconButton';
import { Thumb } from '@/components/atoms/Thumb';
import { CardProfileLabelAvatar } from '@/components/molecules/CardProfileLabelAvatar';
import { MenuItemsList } from '@/components/molecules/MenuItemsList';
import { getLogoByTheme } from '@/utils/assets';
export const SideMenu: FC<SideMenuProps> = ({
  isOpen,
  setIsMenuOpen
}) => {
  const pathname = usePathname();
  const {
    t
  } = useTranslation('common');
  const {
    user
  } = useAuth();
  const {
    colorMode
  } = useColorMode();
  const {
    data: balanceData = balanceFactory
  } = useGetGamificationBalance();
  const {
    mutate: sendClickEvent
  } = useSendClickEvent();
  const handleMenuItemClick = (url: string) => {
    if (!user?.name) {
      window.dispatchEvent(new Event('open-unauthenticated-modal'));
    }
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.sideMenu.label(url)
    });
  };
  const firstName = user?.name.split(' ')[0] ?? 'Usuário convidado';
  const href = pathname === PATHS.home ? '#' : PATHS.home;
  useEffect(() => {
    return () => {
      setIsMenuOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Flex display={{
    base: 'none',
    md: 'flex'
  }} flexDir="column" alignItems="center" w={isOpen ? '19.625rem' : '7.25rem'} h="100vh" p="2.5rem 1.5rem" bgColor="background.brandLight" overflow="scroll" transition="all 500ms ease-in-out" sx={{
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    '&::-mozkit-scrollbar': {
      display: 'none'
    }
  }} onMouseOverCapture={() => setIsMenuOpen(true)} onMouseOutCapture={() => setIsMenuOpen(false)} data-sentry-element="Flex" data-sentry-component="SideMenu" data-sentry-source-file="index.tsx">
      <Flex justifyContent="space-between" alignItems="center" w="full" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <Thumb href={balanceData.unauthorized ? '' : href} onClick={() => handleMenuItemClick(PATHS.home)} src={getLogoByTheme(colorMode === 'dark')} width={72} height={24} full fit="contain" alt="Logo escrito EQI+ em verde escuro" data-sentry-element="Thumb" data-sentry-source-file="index.tsx" />
        {!balanceData.unauthorized && <Link href={balanceData.unauthorized ? '' : href}>
            <Icon iconName="HomeOutline" aria-label="Ir para home" display={isOpen ? 'block' : 'none'} width={24} height={24} color="content.primary" transition="display 1000ms ease-in-out" _hover={{
          color: 'content.brand'
        }} />
          </Link>}
      </Flex>
      <Flex flexDir="column" alignItems={isOpen ? 'flex-start' : 'center'} mt="1.5625rem" mb="0.9375rem" w="full" h={isOpen ? 20 : 10} data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <Box id="box_card_profile_side_menu" display={isOpen ? 'block' : 'none'} transition="display 1000ms ease-in-out" onClick={() => {
        if (!user?.name || balanceData.unauthorized) {
          window.dispatchEvent(new Event('open-unauthenticated-modal'));
          return;
        }
        window.dispatchEvent(new CustomEvent(`open-chakra-modal-${MY_PROFILE_DRAWER_ID}`));
      }} w="full" data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <CardProfileLabelAvatar title={firstName} label={t('screens.menuMobile.menu.seeProfile')} path="#" data-sentry-element="CardProfileLabelAvatar" data-sentry-source-file="index.tsx" />
        </Box>
        <IconButton ariaLabel="Menu" icon={Menu} iconProps={{
        color: 'content.primary',
        w: 24,
        h: 24,
        _hover: {
          color: 'content.brand'
        }
      }} buttonProps={{
        display: isOpen ? 'none' : 'flex',
        h: '3.125rem',
        w: 10,
        transition: 'display 1000ms ease-in-out'
      }} data-sentry-element="IconButton" data-sentry-source-file="index.tsx" />
      </Flex>
      <Divider mt="2rem" mb="1.875rem" w={isOpen ? 'full' : '70%'} borderBottomColor="var(--color-eqi-gray-lighten)" opacity="1" data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
      <MenuItemsList isMenuOpen={isOpen} unauthorized={balanceData.unauthorized} handleMenuItemClick={handleMenuItemClick} data-sentry-element="MenuItemsList" data-sentry-source-file="index.tsx" />
      <Divider my="3" w={isOpen ? 'full' : '70%'} borderBottomColor="var(--color-eqi-gray-lighten)" opacity="1" data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
    </Flex>;
};
type SideMenuProps = {
  isOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
};